import { WebDashboardContentSpecSlicersInner } from "@coeff/agent-types";

import { DateRangeSelection } from "./dateRangeSelection";
import { isAllSelected, SelectOption } from "./slicerValues";
import { SlicerState } from "./useDashboardRuntime";

/** The filter state a "Save as default" is capturing. */
export interface FilterDefaultsSnapshot {
  slicerState: SlicerState;
  /** Structured intent behind each date filter's value — see useDashboardRuntime. */
  dateRangeSelections: Record<string, DateRangeSelection | null>;
  /**
   * The options each select slicer resolved on this load. A multi-select whose value covers
   * all of them is saved as `default_all` (intent), not as the frozen list.
   */
  resolvedOptions?: Record<string, SelectOption[]>;
}

/**
 * Whether the current filter state holds anything worth saving as the default.
 *
 * Not the same question as `isSlicersActive`, which is true whenever ANY filter differs — a
 * `personalized_view` included. That one is an editor's "view as" preview, never part of the
 * dashboard's default, so a changed identity alone must not offer a save that would store
 * nothing.
 */
export const hasSavableDefaultChanges = (
  slicers: WebDashboardContentSpecSlicersInner[],
  slicerState: SlicerState,
  defaultSlicerState: SlicerState
): boolean =>
  slicers.some(
    slicer =>
      slicer.type !== "personalized_view" &&
      JSON.stringify(slicerState[slicer.name]) !== JSON.stringify(defaultSlicerState[slicer.name])
  );

/**
 * The dashboard's slicers with the snapshot's filter state written in as their defaults.
 *
 * A date filter stores its SELECTION, never the dates that selection resolved to — otherwise
 * "Last 30 days" would be saved as a fixed window and the dashboard would open on a stale
 * month forever. A date filter with no structured selection (a data-derived
 * `get_default_value_js_function` default the viewer never touched) keeps its configured
 * default rather than being frozen.
 *
 * `personalized_view` is never touched: it scopes rows to whoever is looking, so there is no
 * such thing as a dashboard-wide default for it.
 *
 * Returns a new array; slicers with nothing to change are passed through by reference.
 */
export const withSavedDefaults = (
  slicers: WebDashboardContentSpecSlicersInner[],
  { slicerState, dateRangeSelections, resolvedOptions }: FilterDefaultsSnapshot
): WebDashboardContentSpecSlicersInner[] =>
  slicers.map(slicer => {
    const value = slicerState[slicer.name];

    if (slicer.type === "date_range") {
      const selection = dateRangeSelections[slicer.name];
      return selection ? { ...slicer, default_selection: selection } : slicer;
    }
    if (slicer.type === "single_select") {
      // An empty value would open the dashboard on nothing selected, rendering every
      // dependent block blank — not something to save as the default.
      return typeof value === "string" && value !== ""
        ? { ...slicer, default_value: value }
        : slicer;
    }
    if (slicer.type === "multi_select") {
      const options = resolvedOptions?.[slicer.name];
      // Without resolved options this load cannot tell "all" from "none" (the options query
      // returned nothing or failed), so leave the slicer's saved default exactly as it is —
      // otherwise a save made for a date filter would persist `[]` and blank every block.
      if (!Array.isArray(value) || !options || options.length === 0) {
        return slicer;
      }
      // "Everything selected" is saved as intent so values that appear later are included
      // too; a frozen full list would leave them unselected while the picker says "All".
      if (isAllSelected(value, options)) {
        return { ...slicer, default_all: true, default_value: null };
      }
      // An empty array IS a meaningful default here ("start with nothing selected").
      return { ...slicer, default_all: false, default_value: value };
    }
    return slicer;
  });
